import type {
  AssetDto,
  CompanyCardDto,
  CompanyDossierDto,
  Confidence,
  SubSegment,
} from '../types';
import {
  acraRef,
  brregRef,
  companiesHouseRef,
  companyRef,
  courtRef,
  edgarRef,
  fitchRef,
  mediaRef,
  regnskapRef,
  unternehmensregisterRef,
} from './sources';

// Phase-A typed fixtures — the acceptance test for types.ts (plan block A2). Every fact
// carries confidence + a SourceRef; absent facts are explicit not_disclosed rows;
// estimated values carry est_basis. ALL COMPANIES ARE FICTIONAL — invented names and
// registry numbers, placeholder data replaced by the real agent-authored seed in A6.

/* ================================================================== assets ==== */

export const ASSETS: AssetDto[] = [
  {
    id: 1,
    companySlug: 'gulf-coast-drilling-systems',
    companyName: 'Gulf Coast Drilling Systems Inc',
    kind: 'auction_lot',
    name: 'Lot 2 — Gulf of Mexico aftermarket service book',
    description:
      '§363 sale lot: ~140 active service agreements for third-party drawworks and pipe-handling equipment across GoM shelf and deepwater customers.',
    status: 'for_sale',
    fitScore: 82,
    confidence: 'confirmed',
    source: courtRef('26-90112', 'Notice of §363 sale and bidding procedures, Docket 214', '2026-06-05'),
  },
  {
    id: 2,
    companySlug: 'gulf-coast-drilling-systems',
    companyName: 'Gulf Coast Drilling Systems Inc',
    kind: 'auction_lot',
    name: 'Lot 1 — Houston manufacturing facility and test stands',
    description: '18,500 m² fabrication hall, two 750 t test stands, overhead cranage to 120 t.',
    status: 'for_sale',
    fitScore: 41,
    confidence: 'confirmed',
    source: courtRef('26-90112', 'Notice of §363 sale and bidding procedures, Docket 214', '2026-06-05'),
  },
  {
    id: 3,
    companySlug: 'nordic-drilltech',
    companyName: 'Nordic Drilltech AS',
    kind: 'product_line',
    name: 'DTX-900 iron roughneck line',
    description: 'Flagship hydraulic roughneck family; ~220 units in the installed base.',
    status: 'tracked',
    fitScore: 88,
    confidence: 'confirmed',
    source: companyRef('nordicdrilltech.example', 'Product catalogue 2026', '2026-01-15', 'products/dtx-900'),
  },
  {
    id: 4,
    companySlug: 'rheinland-antriebstechnik',
    companyName: 'Rheinland Antriebstechnik GmbH',
    kind: 'product_line',
    name: 'RA-Drive drilling drives division',
    description: 'AC drive systems and motor packages for drawworks and mud pumps; under strategic review by the parent.',
    status: 'tracked',
    fitScore: 74,
    confidence: 'reported',
    source: mediaRef('Offshore Engineer', 'Westfalia weighs options for drilling drives arm', '2026-05-12', 'westfalia-strategic-review-fixture'),
  },
  {
    id: 5,
    companySlug: 'aberdeen-rig-components',
    companyName: 'Aberdeen Rig Components Ltd',
    kind: 'aftermarket_book',
    name: 'North Sea BOP recertification contracts',
    description: 'Recurring recertification and spares book across UKCS jack-up operators.',
    status: 'tracked',
    fitScore: 71,
    confidence: 'estimated',
    source: companiesHouseRef('SC334455', 'Annual accounts FY2025 (segment note)', '2026-02-28'),
  },
  {
    id: 6,
    companySlug: 'meridian-well-automation',
    companyName: 'Meridian Well Automation Pte Ltd',
    kind: 'product_line',
    name: 'WellPilot autonomous drilling advisor',
    description: 'Real-time drilling optimisation software; 14 rigs under subscription.',
    status: 'tracked',
    fitScore: 85,
    confidence: 'confirmed',
    source: companyRef('meridianwell.example', 'WellPilot product page', '2026-03-02', 'wellpilot'),
  },
  {
    id: 7,
    companySlug: 'pennine-drive-systems',
    companyName: 'Pennine Drive Systems plc',
    kind: 'facility',
    name: 'Sheffield gear plant',
    description: 'Precision gearbox machining for drawworks and top-drive transmissions.',
    status: 'tracked',
    fitScore: 46,
    confidence: 'confirmed',
    source: companiesHouseRef('01234987', 'Annual report FY2025 — operations review', '2026-03-30'),
  },
];

/* ============================================================ full dossiers ==== */

const DOSSIER_LIST: Omit<CompanyDossierDto, 'subSegments'>[] = [
  // ── 1. Nordic Drilltech AS — the clean high-fit family-owned Norwegian OEM ──
  {
    slug: 'nordic-drilltech',
    name: 'Nordic Drilltech AS',
    nameAliases: ['Nordic Drilltech'],
    formerNames: ['Sunnmøre Boreutstyr AS'],
    website: 'https://www.nordicdrilltech.example',
    description:
      'Family-owned Norwegian OEM of pipe-handling and drill-floor tools (iron roughnecks, catwalks, tubular feeders) with a growing aftermarket book in the North Sea.',
    hqCountry: 'NO',
    operatingCountries: ['NO', 'GB', 'BR'],
    legalIds: { lei: null, noOrgnr: '985612340', ukCrn: null, usCik: null, deHrb: null },
    ticker: null,
    ownershipType: 'family_private',
    ultimateParentSlug: null,
    peSponsor: null,
    peEntryYear: null,
    sizeBand: 'mid',
    status: 'active',
    distressStage: 'none',
    watchlistStage: 'qualified',
    facts: [
      { key: 'revenue', value: 'NOK 1 384m', valueNum: 128, unit: 'USD M', fy: 2025, currency: 'NOK', confidence: 'confirmed', source: regnskapRef('985612340', 2025) },
      { key: 'revenue', value: 'NOK 1 211m', valueNum: 112, unit: 'USD M', fy: 2024, currency: 'NOK', confidence: 'confirmed', source: regnskapRef('985612340', 2024) },
      { key: 'revenue', value: 'NOK 1 032m', valueNum: 96, unit: 'USD M', fy: 2023, currency: 'NOK', confidence: 'confirmed', source: regnskapRef('985612340', 2023) },
      { key: 'ebitda', value: 'NOK 212m', valueNum: 19.6, unit: 'USD M', fy: 2025, currency: 'NOK', confidence: 'confirmed', source: regnskapRef('985612340', 2025) },
      { key: 'employees', value: '410', valueNum: 410, fy: 2025, confidence: 'confirmed', source: brregRef('985612340', 'Enhetsregisteret record', '2026-04-11') },
      { key: 'net_debt', value: null, valueNum: null, fy: 2025, confidence: 'not_disclosed', source: null },
      { key: 'product_lines', value: 'Iron roughnecks, catwalk machines, tubular feeding systems, drill-floor manipulators', confidence: 'confirmed', source: companyRef('nordicdrilltech.example', 'Product catalogue 2026', '2026-01-15', 'products') },
      { key: 'key_products', value: 'DTX-900 iron roughneck; CW-40 catwalk', confidence: 'confirmed', source: companyRef('nordicdrilltech.example', 'Product catalogue 2026', '2026-01-15', 'products') },
      { key: 'oem_service_mix', value: '~70% OEM / 30% aftermarket', confidence: 'estimated', estBasis: 'Segment split inferred from FY2025 accounts note 4 (service revenue line)', source: regnskapRef('985612340', 2025) },
      { key: 'install_base', value: '≈220 DTX-series units in service', confidence: 'reported', source: mediaRef('Offshore Engineer', 'Nordic Drilltech passes 200 roughneck deliveries', '2025-09-18', 'nordic-drilltech-200-fixture') },
      { key: 'certifications', value: 'ISO 9001, DNV-OS-E101 type approval (drill-floor tools)', confidence: 'confirmed', source: companyRef('nordicdrilltech.example', 'Certifications page', '2026-01-15', 'about/certifications') },
      { key: 'key_customers', value: null, confidence: 'not_disclosed', source: null },
      { key: 'geographic_footprint', value: 'Ålesund (HQ + works), Aberdeen service base, Macaé service base', confidence: 'confirmed', source: companyRef('nordicdrilltech.example', 'Locations', '2026-01-15', 'about/locations') },
      { key: 'founded_year', value: '1987', valueNum: 1987, confidence: 'confirmed', source: brregRef('985612340', 'Enhetsregisteret record', '2026-04-11') },
      { key: 'leadership_ceo', value: 'Ingrid Volden', confidence: 'confirmed', source: brregRef('985612340', 'Enhetsregisteret — roller', '2026-04-11') },
      { key: 'credit_rating', value: null, confidence: 'not_disclosed', source: null },
    ],
    events: [
      { id: 101, kind: 'major_contract_win_loss', eventDate: '2026-05-28', title: 'Frame agreement for drill-floor tool packages on four NCS jack-ups', confidence: 'reported', source: mediaRef('Offshore Engineer', 'Nordic Drilltech lands four-rig tool package', '2026-05-28', 'nordic-drilltech-frame-fixture') },
      { id: 102, kind: 'certification_gained', eventDate: '2026-02-10', title: 'DNV type approval renewed for DTX-900 series', confidence: 'confirmed', source: companyRef('nordicdrilltech.example', 'Press release — DNV renewal', '2026-02-10', 'news/dnv-2026') },
      { id: 103, kind: 'leadership_change', eventDate: '2025-08-15', title: 'Ingrid Volden appointed CEO (second-generation family handover)', confidence: 'confirmed', source: brregRef('985612340', 'Enhetsregisteret — rolleendring', '2025-08-15') },
    ],
    ownershipHistory: [
      { position: 1, name: 'Sunnmøre Boreutstyr AS', owner: 'Volden family', fromDate: '1987', toDate: '2004', dateConfidence: 'stated', source: brregRef('985612340', 'Enhetsregisteret — historikk', '2026-04-11') },
      { position: 2, name: 'Nordic Drilltech AS', owner: 'Volden Holding AS (family)', fromDate: '2004', toDate: null, dateConfidence: 'stated', source: brregRef('985612340', 'Enhetsregisteret — historikk', '2026-04-11') },
    ],
    score: {
      fitScore: 84,
      availabilityScore: 38,
      composite: 61,
      rubricVersion: 'v1',
      scoredAt: '2026-06-24T03:10:00Z',
      fitDimensions: [
        { dimension: 'product_adjacency', score: 92, rationale: 'Pipe-handling and drill-floor tools sit directly adjacent to the HMH drilling-equipment portfolio with minimal overlap in roughneck models.', citations: [{ kind: 'fact', refId: 9007, label: 'product_lines (company catalogue 2026)' }] },
        { dimension: 'aftermarket_attach', score: 78, rationale: '≈220-unit installed base with a 30% service share gives a real attach surface for the combined aftermarket network.', citations: [{ kind: 'fact', refId: 9010, label: 'install_base (Offshore Engineer 2025-09-18)' }, { kind: 'fact', refId: 9009, label: 'oem_service_mix (FY2025 accounts note 4)' }] },
        { dimension: 'technology_differentiation', score: 74, rationale: 'DNV-type-approved tool family with recent renewal; differentiated catwalk automation but no software moat.', citations: [{ kind: 'event', refId: 102, label: 'certification_gained 2026-02-10' }] },
        { dimension: 'size_feasibility', score: 88, rationale: 'Mid-band revenue (USD ~128M FY2025) is comfortably inside the acquirable envelope.', citations: [{ kind: 'fact', refId: 9001, label: 'revenue FY2025 (Regnskapsregisteret)' }] },
      ],
    },
    scoreHistory: [
      { scoredAt: '2026-03-24T03:10:00Z', fitScore: 82, availabilityScore: 40, rubricVersion: 'v1' },
      { scoredAt: '2026-04-24T03:10:00Z', fitScore: 83, availabilityScore: 39, rubricVersion: 'v1' },
      { scoredAt: '2026-05-24T03:10:00Z', fitScore: 84, availabilityScore: 38, rubricVersion: 'v1' },
      { scoredAt: '2026-06-24T03:10:00Z', fitScore: 84, availabilityScore: 38, rubricVersion: 'v1' },
    ],
    assets: ASSETS.filter((a) => a.companySlug === 'nordic-drilltech'),
    watchlistHistory: [
      { stage: 'radar', at: '2026-03-24T09:00:00Z', note: 'Auto-added on first scoring run (fit 82).' },
      { stage: 'watch', at: '2026-04-30T14:20:00Z', note: 'Promoted after DNV renewal and strong FY2025 accounts.' },
      { stage: 'qualified', at: '2026-06-12T10:05:00Z', note: 'Corp-dev review — clean family ownership, credible succession-era seller.' },
    ],
    notes: [
      { id: 1, at: '2026-06-12T10:08:00Z', text: 'Volden family reportedly open to industrial-partner conversations — soft signal via the Aberdeen service-base contact.' },
      { id: 2, at: '2026-05-02T08:45:00Z', text: 'Check overlap of the DTX-900 with the existing roughneck line before any approach.' },
    ],
  },

  // ── 2. Aberdeen Rig Components Ltd — PE-owned, mature hold (entry 2019) ──
  {
    slug: 'aberdeen-rig-components',
    name: 'Aberdeen Rig Components Ltd',
    nameAliases: ['ARC'],
    formerNames: [],
    website: 'https://www.arcomponents.example',
    description:
      'UK supplier of BOP control components, valve assemblies and recertification services for North Sea jack-ups. PE-owned since 2019 — a maturing hold.',
    hqCountry: 'GB',
    operatingCountries: ['GB', 'NL', 'DK'],
    legalIds: { lei: null, noOrgnr: null, ukCrn: 'SC334455', usCik: null, deHrb: null },
    ticker: null,
    ownershipType: 'pe_owned',
    ultimateParentSlug: null,
    peSponsor: 'Grampian Equity Partners',
    peEntryYear: 2019,
    sizeBand: 'small',
    status: 'active',
    distressStage: 'early_signal',
    watchlistStage: 'engaged',
    facts: [
      { key: 'revenue', value: '£32.4m', valueNum: 41, unit: 'USD M', fy: 2025, currency: 'GBP', confidence: 'confirmed', source: companiesHouseRef('SC334455', 'Annual accounts FY2025 (iXBRL)', '2026-02-28') },
      { key: 'revenue', value: '£30.1m', valueNum: 38, unit: 'USD M', fy: 2024, currency: 'GBP', confidence: 'confirmed', source: companiesHouseRef('SC334455', 'Annual accounts FY2024 (iXBRL)', '2025-02-27') },
      { key: 'ebitda', value: '£4.8m', valueNum: 6.1, unit: 'USD M', fy: 2025, currency: 'GBP', confidence: 'confirmed', source: companiesHouseRef('SC334455', 'Annual accounts FY2025 (iXBRL)', '2026-02-28') },
      { key: 'net_debt', value: '£21m', valueNum: 27, unit: 'USD M', fy: 2025, currency: 'GBP', confidence: 'estimated', estBasis: 'Sum of bank loans + shareholder loan notes in FY2025 accounts, less cash', source: companiesHouseRef('SC334455', 'Annual accounts FY2025 (iXBRL)', '2026-02-28') },
      { key: 'employees', value: '180', valueNum: 180, fy: 2025, confidence: 'confirmed', source: companiesHouseRef('SC334455', 'Annual accounts FY2025 — employee note', '2026-02-28') },
      { key: 'product_lines', value: 'BOP control pods, valve assemblies, recertification services', confidence: 'confirmed', source: companyRef('arcomponents.example', 'Capabilities brochure', '2025-11-01', 'capabilities') },
      { key: 'aftermarket_revenue_share', value: '~60%', confidence: 'estimated', estBasis: 'Recertification + spares lines vs product sales in FY2025 segment note', source: companiesHouseRef('SC334455', 'Annual accounts FY2025 (segment note)', '2026-02-28') },
      { key: 'key_customers', value: 'North Sea jack-up contractors (three customers >10% of revenue)', confidence: 'confirmed', source: companiesHouseRef('SC334455', 'Annual accounts FY2025 — concentration note', '2026-02-28') },
      { key: 'customer_concentration', value: 'Top 3 ≈ 55% of revenue', confidence: 'confirmed', source: companiesHouseRef('SC334455', 'Annual accounts FY2025 — concentration note', '2026-02-28') },
      { key: 'founded_year', value: '1998', valueNum: 1998, confidence: 'confirmed', source: companiesHouseRef('SC334455', 'Incorporation record', '1998-05-12') },
      { key: 'pe_fund_vintage', value: 'Grampian Fund III (2018 vintage)', confidence: 'reported', source: mediaRef('Upstream', 'Grampian buys Aberdeen Rig Components', '2019-06-20', 'grampian-arc-fixture') },
      { key: 'leadership_ceo', value: 'Fiona McAllister', confidence: 'confirmed', source: companiesHouseRef('SC334455', 'Officers list', '2026-01-10') },
      { key: 'credit_rating', value: null, confidence: 'not_disclosed', source: null },
    ],
    events: [
      { id: 111, kind: 'pe_hold_mature', eventDate: '2026-06-20', title: 'PE hold reached 7 years (entry 2019) — computed exit-pressure signal', confidence: 'estimated', source: null },
      { id: 112, kind: 'covenant_breach', eventDate: '2026-04-02', title: 'FY2025 accounts disclose covenant waiver obtained from lender', confidence: 'confirmed', source: companiesHouseRef('SC334455', 'Annual accounts FY2025 — going concern note', '2026-02-28') },
      { id: 113, kind: 'banker_mandate_reported', eventDate: '2026-06-11', title: 'Sponsor reported to have mandated advisers for a sale of ARC', confidence: 'reported', source: mediaRef('Offshore Engineer', 'Grampian said to test market for ARC', '2026-06-11', 'grampian-arc-sale-fixture') },
    ],
    ownershipHistory: [
      { position: 1, name: 'Aberdeen Rig Components Ltd', owner: 'Founders (McAllister family)', fromDate: '1998', toDate: '2019-06', dateConfidence: 'stated', source: companiesHouseRef('SC334455', 'PSC register history', '2026-01-10') },
      { position: 2, name: 'Aberdeen Rig Components Ltd', owner: 'Grampian Equity Partners (Fund III)', fromDate: '2019-06', toDate: null, dateConfidence: 'stated', source: companiesHouseRef('SC334455', 'PSC register history', '2026-01-10') },
    ],
    score: {
      fitScore: 77,
      availabilityScore: 66,
      composite: 71,
      rubricVersion: 'v1',
      scoredAt: '2026-06-24T03:10:00Z',
      fitDimensions: [
        { dimension: 'product_adjacency', score: 70, rationale: 'BOP controls and valves complement pressure-control offerings without model overlap.', citations: [{ kind: 'fact', refId: 9106, label: 'product_lines (capabilities brochure)' }] },
        { dimension: 'aftermarket_attach', score: 84, rationale: 'Majority-aftermarket revenue with a recurring recertification book — directly monetisable through a larger service network.', citations: [{ kind: 'fact', refId: 9107, label: 'aftermarket_revenue_share (FY2025 segment note)' }] },
        { dimension: 'technology_differentiation', score: 58, rationale: 'Solid engineering but commodity-adjacent valve scope; differentiation is certification and response time.', citations: [{ kind: 'fact', refId: 9106, label: 'product_lines (capabilities brochure)' }] },
        { dimension: 'size_feasibility', score: 90, rationale: 'Small band (USD ~41M revenue) — a bolt-on.', citations: [{ kind: 'fact', refId: 9101, label: 'revenue FY2025 (Companies House iXBRL)' }] },
      ],
    },
    scoreHistory: [
      { scoredAt: '2026-03-24T03:10:00Z', fitScore: 74, availabilityScore: 48, rubricVersion: 'v1' },
      { scoredAt: '2026-04-24T03:10:00Z', fitScore: 74, availabilityScore: 55, rubricVersion: 'v1' },
      { scoredAt: '2026-05-24T03:10:00Z', fitScore: 74, availabilityScore: 57, rubricVersion: 'v1' },
      { scoredAt: '2026-06-24T03:10:00Z', fitScore: 77, availabilityScore: 66, rubricVersion: 'v1' },
    ],
    assets: ASSETS.filter((a) => a.companySlug === 'aberdeen-rig-components'),
    watchlistHistory: [
      { stage: 'radar', at: '2026-03-24T09:00:00Z', note: null },
      { stage: 'watch', at: '2026-04-06T11:30:00Z', note: 'Covenant waiver in FY2025 accounts — availability rising.' },
      { stage: 'qualified', at: '2026-05-15T13:00:00Z', note: 'Aftermarket book fits the North Sea service-network thesis.' },
      { stage: 'engaged', at: '2026-06-13T09:30:00Z', note: 'Adviser mandate reported — requested process entry via Grampian.' },
    ],
    notes: [
      { id: 11, at: '2026-06-25T15:10:00Z', text: 'NDA executed; teaser expected week of 2026-07-06.' },
      { id: 12, at: '2026-06-13T09:35:00Z', text: 'Sponsor is seven years into Fund III — motivated seller, but the leverage means price expectations may still be anchored high.' },
    ],
  },

  // ── 3. Gulf Coast Drilling Systems — Chapter 11, §363 auction running ──
  {
    slug: 'gulf-coast-drilling-systems',
    name: 'Gulf Coast Drilling Systems Inc',
    nameAliases: ['GCDS'],
    formerNames: ['Bayou Drilling Equipment Co.'],
    website: 'https://www.gcdrillingsystems.example',
    description:
      'US manufacturer of drawworks and pipe-handling equipment with a large Gulf of Mexico aftermarket book. Filed Chapter 11 in March 2026; a §363 asset auction is in progress.',
    hqCountry: 'US',
    operatingCountries: ['US', 'MX'],
    legalIds: { lei: '549300FIXTURE00GC01', noOrgnr: null, ukCrn: null, usCik: '0001998877', deHrb: null },
    ticker: 'GCDS (delisted)',
    ownershipType: 'public',
    ultimateParentSlug: null,
    peSponsor: null,
    peEntryYear: null,
    sizeBand: 'mid',
    status: 'in_insolvency',
    distressStage: 'in_process',
    watchlistStage: 'qualified',
    facts: [
      { key: 'revenue', value: '$96.2m', valueNum: 96.2, unit: 'USD M', fy: 2025, currency: 'USD', confidence: 'confirmed', source: edgarRef('0001998877', 'Form 10-K FY2025', '2026-02-24') },
      { key: 'revenue', value: '$121.5m', valueNum: 121.5, unit: 'USD M', fy: 2024, currency: 'USD', confidence: 'confirmed', source: edgarRef('0001998877', 'Form 10-K FY2024', '2025-02-25') },
      { key: 'ebitda', value: '-$8.1m', valueNum: -8.1, unit: 'USD M', fy: 2025, currency: 'USD', confidence: 'confirmed', source: edgarRef('0001998877', 'Form 10-K FY2025', '2026-02-24') },
      { key: 'net_debt', value: '$214m', valueNum: 214, unit: 'USD M', fy: 2025, currency: 'USD', confidence: 'confirmed', source: edgarRef('0001998877', 'Form 10-K FY2025', '2026-02-24') },
      { key: 'employees', value: '350', valueNum: 350, fy: 2025, confidence: 'confirmed', source: edgarRef('0001998877', 'Form 10-K FY2025', '2026-02-24') },
      { key: 'product_lines', value: 'Drawworks (2,000–4,500 hp), pipe-handling columns, catwalks; GoM service and spares', confidence: 'confirmed', source: edgarRef('0001998877', 'Form 10-K FY2025 — business section', '2026-02-24') },
      { key: 'install_base', value: '~90 drawworks units in the Americas', confidence: 'estimated', estBasis: 'Cumulative deliveries per 10-K business sections FY2019–FY2025', source: edgarRef('0001998877', 'Form 10-K FY2025 — business section', '2026-02-24') },
      { key: 'aftermarket_revenue_share', value: '44%', confidence: 'confirmed', source: edgarRef('0001998877', 'Form 10-K FY2025 — segment data', '2026-02-24') },
      { key: 'key_customers', value: null, confidence: 'not_disclosed', source: null },
      { key: 'founded_year', value: '1979', valueNum: 1979, confidence: 'confirmed', source: edgarRef('0001998877', 'Form 10-K FY2025 — business section', '2026-02-24') },
      { key: 'credit_rating', value: 'CCC- (downgraded from B-)', confidence: 'confirmed', source: fitchRef('Fitch downgrades Gulf Coast Drilling Systems to CCC-', '2026-01-22') },
      { key: 'listed_exchange', value: 'NYSE (delisting notice Feb 2026)', confidence: 'confirmed', source: edgarRef('0001998877', 'Form 8-K — NYSE notice', '2026-02-10') },
    ],
    events: [
      { id: 121, kind: 'asset_auction_363', eventDate: '2026-06-05', title: '§363 auction noticed — two lots incl. GoM aftermarket book; bids due 2026-07-20', jurisdiction: 'US', payload: { court: 'Bankr. S.D. Tex.', caseNo: '26-90112', lots: 2, bidDeadline: '2026-07-20' }, confidence: 'confirmed', source: courtRef('26-90112', 'Notice of §363 sale and bidding procedures, Docket 214', '2026-06-05') },
      { id: 122, kind: 'bankruptcy_filing', eventDate: '2026-03-14', title: 'Chapter 11 petition filed in Bankr. S.D. Tex. (case 26-90112)', jurisdiction: 'US', payload: { court: 'Bankr. S.D. Tex.', caseNo: '26-90112', processType: 'Chapter 11' }, confidence: 'confirmed', source: courtRef('26-90112', 'Voluntary petition, Docket 1', '2026-03-14') },
      { id: 123, kind: 'delisting_notice', eventDate: '2026-02-10', title: 'NYSE continued-listing standard notice (8-K item 3.01)', jurisdiction: 'US', confidence: 'confirmed', source: edgarRef('0001998877', 'Form 8-K — NYSE notice', '2026-02-10') },
      { id: 124, kind: 'credit_downgrade', eventDate: '2026-01-22', title: 'Fitch downgrade to CCC- on refinancing risk', confidence: 'confirmed', source: fitchRef('Fitch downgrades Gulf Coast Drilling Systems to CCC-', '2026-01-22') },
      { id: 125, kind: 'going_concern_flag', eventDate: '2025-11-20', title: 'Substantial-doubt going-concern language in Q3 10-Q', confidence: 'confirmed', source: edgarRef('0001998877', 'Form 10-Q Q3 2025', '2025-11-20') },
    ],
    ownershipHistory: [
      { position: 1, name: 'Bayou Drilling Equipment Co.', owner: 'Founders', fromDate: '1979', toDate: '2011', dateConfidence: 'estimated', source: edgarRef('0001998877', 'Form S-1 — history section', '2014-05-02') },
      { position: 2, name: 'Gulf Coast Drilling Systems Inc', owner: 'Public (NYSE: GCDS)', fromDate: '2014-06', toDate: null, dateConfidence: 'stated', source: edgarRef('0001998877', 'Form S-1', '2014-05-02') },
    ],
    score: {
      fitScore: 71,
      availabilityScore: 92,
      composite: 80,
      rubricVersion: 'v1',
      scoredAt: '2026-06-24T03:10:00Z',
      fitDimensions: [
        { dimension: 'product_adjacency', score: 66, rationale: 'Drawworks overlap with existing lines; the pipe-handling column business and GoM service footprint are the adjacent value.', citations: [{ kind: 'fact', refId: 9206, label: 'product_lines (10-K FY2025)' }] },
        { dimension: 'aftermarket_attach', score: 80, rationale: '44% aftermarket share and the Lot-2 service book is separable at auction.', citations: [{ kind: 'fact', refId: 9208, label: 'aftermarket_revenue_share (10-K FY2025)' }, { kind: 'event', refId: 121, label: 'asset_auction_363 2026-06-05' }] },
        { dimension: 'technology_differentiation', score: 52, rationale: 'Mature product family; value is installed base, not technology.', citations: [{ kind: 'fact', refId: 9207, label: 'install_base (10-K cumulative deliveries)' }] },
        { dimension: 'size_feasibility', score: 85, rationale: 'Whole-co mid band, but §363 lots allow an asset-level entry at small-band size.', citations: [{ kind: 'event', refId: 121, label: 'asset_auction_363 2026-06-05' }] },
      ],
    },
    scoreHistory: [
      { scoredAt: '2026-03-24T03:10:00Z', fitScore: 71, availabilityScore: 71, rubricVersion: 'v1' },
      { scoredAt: '2026-04-24T03:10:00Z', fitScore: 71, availabilityScore: 74, rubricVersion: 'v1' },
      { scoredAt: '2026-05-24T03:10:00Z', fitScore: 71, availabilityScore: 78, rubricVersion: 'v1' },
      { scoredAt: '2026-06-24T03:10:00Z', fitScore: 71, availabilityScore: 92, rubricVersion: 'v1' },
    ],
    assets: ASSETS.filter((a) => a.companySlug === 'gulf-coast-drilling-systems'),
    watchlistHistory: [
      { stage: 'radar', at: '2026-03-24T09:00:00Z', note: null },
      { stage: 'qualified', at: '2026-06-08T16:00:00Z', note: 'Skipped watch — the §363 timeline forces a decision before the 2026-07-20 bid deadline; Lot 2 is the target.' },
    ],
    notes: [
      { id: 21, at: '2026-06-26T12:00:00Z', text: 'Counsel engaged on bidding-procedures review; qualified-bidder deadline 2026-07-13.' },
    ],
  },

  // ── 4. Rheinland Antriebstechnik — conglomerate division under strategic review ──
  {
    slug: 'rheinland-antriebstechnik',
    name: 'Rheinland Antriebstechnik GmbH',
    nameAliases: ['RA Drives'],
    formerNames: [],
    website: 'https://www.ra-drives.example',
    description:
      'German drives and motors specialist (AC drive systems for drawworks, mud pumps and top drives) — a division of Westfalia Industriegruppe, publicly under strategic review since May 2026.',
    hqCountry: 'DE',
    operatingCountries: ['DE', 'NO', 'SG', 'US'],
    legalIds: { lei: null, noOrgnr: null, ukCrn: null, usCik: null, deHrb: 'HRB 66432' },
    ticker: null,
    ownershipType: 'conglomerate_division',
    ultimateParentSlug: null,
    peSponsor: null,
    peEntryYear: null,
    sizeBand: 'mid',
    status: 'active',
    distressStage: 'early_signal',
    watchlistStage: 'watch',
    facts: [
      { key: 'revenue', value: '€172m', valueNum: 187, unit: 'USD M', fy: 2025, currency: 'EUR', confidence: 'estimated', estBasis: 'Parent segment reporting — drilling drives share of the Drives segment per FY2025 annual report', source: unternehmensregisterRef('HRB66432', 'Westfalia Industriegruppe annual report FY2025', '2026-04-15') },
      { key: 'ebitda_margin_pct', value: '9–11%', valueNum: 10, unit: '%', fy: 2025, confidence: 'estimated', estBasis: 'Parent Drives-segment margin band applied to the division', source: unternehmensregisterRef('HRB66432', 'Westfalia Industriegruppe annual report FY2025', '2026-04-15') },
      { key: 'employees', value: '720', valueNum: 720, fy: 2025, confidence: 'reported', source: mediaRef('Offshore Engineer', 'Westfalia weighs options for drilling drives arm', '2026-05-12', 'westfalia-strategic-review-fixture') },
      { key: 'net_debt', value: null, confidence: 'not_disclosed', source: null },
      { key: 'product_lines', value: 'AC drive systems, drilling motor packages, power-control rooms', confidence: 'confirmed', source: companyRef('ra-drives.example', 'Products overview', '2026-01-20', 'products') },
      { key: 'electrification_capabilities', value: 'Shore-power and battery-hybrid retrofit packages for jack-ups', confidence: 'confirmed', source: companyRef('ra-drives.example', 'Electrification solutions', '2026-01-20', 'electrification') },
      { key: 'parent_company', value: 'Westfalia Industriegruppe AG', confidence: 'confirmed', source: unternehmensregisterRef('HRB66432', 'HRB record', '2026-04-15') },
      { key: 'founded_year', value: '1962', valueNum: 1962, confidence: 'confirmed', source: unternehmensregisterRef('HRB66432', 'HRB record', '2026-04-15') },
      { key: 'key_customers', value: null, confidence: 'not_disclosed', source: null },
    ],
    events: [
      { id: 131, kind: 'strategic_review', eventDate: '2026-05-12', title: 'Parent announces strategic review of the drilling drives division', confidence: 'reported', source: mediaRef('Offshore Engineer', 'Westfalia weighs options for drilling drives arm', '2026-05-12', 'westfalia-strategic-review-fixture') },
      { id: 132, kind: 'non_core_designation', eventDate: '2026-04-15', title: 'FY2025 annual report labels drilling drives "non-core"', confidence: 'confirmed', source: unternehmensregisterRef('HRB66432', 'Westfalia Industriegruppe annual report FY2025', '2026-04-15') },
    ],
    ownershipHistory: [
      { position: 1, name: 'Rheinland Antriebstechnik GmbH', owner: 'Independent (family)', fromDate: '1962', toDate: '1994', dateConfidence: 'estimated', source: unternehmensregisterRef('HRB66432', 'HRB record — history', '2026-04-15') },
      { position: 2, name: 'Rheinland Antriebstechnik GmbH', owner: 'Westfalia Industriegruppe AG', fromDate: '1994', toDate: null, dateConfidence: 'stated', source: unternehmensregisterRef('HRB66432', 'HRB record — history', '2026-04-15') },
    ],
    score: {
      fitScore: 69,
      availabilityScore: 71,
      composite: 70,
      rubricVersion: 'v1',
      scoredAt: '2026-06-24T03:10:00Z',
      fitDimensions: [
        { dimension: 'product_adjacency', score: 72, rationale: 'Drive systems power the equipment HMH builds — vertical adjacency rather than overlap.', citations: [{ kind: 'fact', refId: 9305, label: 'product_lines (products overview)' }] },
        { dimension: 'aftermarket_attach', score: 55, rationale: 'Service share undisclosed; drives retrofits imply some recurring work.', citations: [{ kind: 'fact', refId: 9306, label: 'electrification_capabilities (company page)' }] },
        { dimension: 'technology_differentiation', score: 76, rationale: 'Battery-hybrid retrofit packages are a differentiated electrification play.', citations: [{ kind: 'fact', refId: 9306, label: 'electrification_capabilities (company page)' }] },
        { dimension: 'size_feasibility', score: 62, rationale: 'Upper-mid band carve-out — feasible but not a bolt-on; carve-out complexity priced in.', citations: [{ kind: 'fact', refId: 9301, label: 'revenue FY2025 (parent segment estimate)' }] },
      ],
    },
    scoreHistory: [
      { scoredAt: '2026-03-24T03:10:00Z', fitScore: 71, availabilityScore: 42, rubricVersion: 'v1' },
      { scoredAt: '2026-04-24T03:10:00Z', fitScore: 71, availabilityScore: 51, rubricVersion: 'v1' },
      { scoredAt: '2026-05-24T03:10:00Z', fitScore: 69, availabilityScore: 57, rubricVersion: 'v1' },
      { scoredAt: '2026-06-24T03:10:00Z', fitScore: 69, availabilityScore: 71, rubricVersion: 'v1' },
    ],
    assets: ASSETS.filter((a) => a.companySlug === 'rheinland-antriebstechnik'),
    watchlistHistory: [],
    notes: [],
  },

  // ── 5. Meridian Well Automation — SG software/automation, PE-owned (entry 2020) ──
  {
    slug: 'meridian-well-automation',
    name: 'Meridian Well Automation Pte Ltd',
    nameAliases: ['Meridian'],
    formerNames: ['DrillSense Analytics Pte Ltd'],
    website: 'https://www.meridianwell.example',
    description:
      'Singapore drilling-automation software house — autonomous drilling advisory, rig-state detection and remote operations tooling under rig-count subscriptions.',
    hqCountry: 'SG',
    operatingCountries: ['SG', 'MY', 'AE', 'US'],
    legalIds: { lei: null, noOrgnr: null, ukCrn: null, usCik: null, deHrb: null },
    ticker: null,
    ownershipType: 'pe_owned',
    ultimateParentSlug: null,
    peSponsor: 'Tanjong Growth Partners',
    peEntryYear: 2020,
    sizeBand: 'small',
    status: 'active',
    distressStage: 'none',
    watchlistStage: 'watch',
    facts: [
      { key: 'revenue', value: 'S$38m', valueNum: 28, unit: 'USD M', fy: 2025, currency: 'SGD', confidence: 'reported', source: mediaRef('Offshore Engineer', 'Meridian doubles rig subscriptions in two years', '2026-03-08', 'meridian-subscriptions-fixture') },
      { key: 'employees', value: '95', valueNum: 95, fy: 2025, confidence: 'confirmed', source: acraRef('200912345K', 'BizFile business profile', '2026-02-14') },
      { key: 'ebitda', value: null, fy: 2025, confidence: 'not_disclosed', source: null },
      { key: 'software_products', value: 'WellPilot advisor, RigState detection engine, RemoteOps console', confidence: 'confirmed', source: companyRef('meridianwell.example', 'Products', '2026-03-02', 'products') },
      { key: 'automation_capabilities', value: 'Closed-loop autodriller integration on 14 rigs (subscription)', confidence: 'confirmed', source: companyRef('meridianwell.example', 'WellPilot product page', '2026-03-02', 'wellpilot') },
      { key: 'ip_patents', value: '6 granted patents (rig-state classification)', confidence: 'reported', source: mediaRef('Offshore Engineer', 'Meridian doubles rig subscriptions in two years', '2026-03-08', 'meridian-subscriptions-fixture') },
      { key: 'key_customers', value: null, confidence: 'not_disclosed', source: null },
      { key: 'founded_year', value: '2009', valueNum: 2009, confidence: 'confirmed', source: acraRef('200912345K', 'BizFile business profile', '2026-02-14') },
      { key: 'pe_fund_vintage', value: 'Tanjong Fund II (2019 vintage)', confidence: 'reported', source: mediaRef('Upstream', 'Tanjong backs DrillSense buyout', '2020-09-30', 'tanjong-drillsense-fixture') },
      { key: 'leadership_ceo', value: 'Daniel Yeo', confidence: 'confirmed', source: acraRef('200912345K', 'BizFile business profile — officers', '2026-02-14') },
    ],
    events: [
      { id: 141, kind: 'pe_hold_mature', eventDate: '2026-06-20', title: 'PE hold reached 6 years (entry 2020) — computed exit-pressure signal', confidence: 'estimated', source: null },
      { id: 142, kind: 'product_launch', eventDate: '2026-03-02', title: 'WellPilot 3.0 launched with closed-loop autodriller control', confidence: 'confirmed', source: companyRef('meridianwell.example', 'Press release — WellPilot 3.0', '2026-03-02', 'news/wellpilot-3') },
    ],
    ownershipHistory: [
      { position: 1, name: 'DrillSense Analytics Pte Ltd', owner: 'Founders', fromDate: '2009', toDate: '2020-09', dateConfidence: 'stated', source: acraRef('200912345K', 'BizFile — historical extracts', '2026-02-14') },
      { position: 2, name: 'Meridian Well Automation Pte Ltd', owner: 'Tanjong Growth Partners (Fund II)', fromDate: '2020-09', toDate: null, dateConfidence: 'stated', source: acraRef('200912345K', 'BizFile — historical extracts', '2026-02-14') },
    ],
    score: {
      fitScore: 81,
      availabilityScore: 55,
      composite: 68,
      rubricVersion: 'v1',
      scoredAt: '2026-06-24T03:10:00Z',
      fitDimensions: [
        { dimension: 'product_adjacency', score: 78, rationale: 'Automation software layers directly onto the drilling-controls stack.', citations: [{ kind: 'fact', refId: 9404, label: 'software_products (company page)' }] },
        { dimension: 'aftermarket_attach', score: 68, rationale: 'Subscription model = recurring revenue attachable to the installed base.', citations: [{ kind: 'fact', refId: 9405, label: 'automation_capabilities (WellPilot page)' }] },
        { dimension: 'technology_differentiation', score: 90, rationale: 'Patented rig-state classification and a working closed-loop advisor — scarce capability.', citations: [{ kind: 'fact', refId: 9406, label: 'ip_patents (Offshore Engineer 2026-03-08)' }, { kind: 'event', refId: 142, label: 'product_launch 2026-03-02' }] },
        { dimension: 'size_feasibility', score: 86, rationale: 'Small band; typical technology bolt-on size.', citations: [{ kind: 'fact', refId: 9401, label: 'revenue FY2025 (reported)' }] },
      ],
    },
    scoreHistory: [
      { scoredAt: '2026-03-24T03:10:00Z', fitScore: 77, availabilityScore: 55, rubricVersion: 'v1' },
      { scoredAt: '2026-04-24T03:10:00Z', fitScore: 79, availabilityScore: 55, rubricVersion: 'v1' },
      { scoredAt: '2026-05-24T03:10:00Z', fitScore: 81, availabilityScore: 55, rubricVersion: 'v1' },
      { scoredAt: '2026-06-24T03:10:00Z', fitScore: 81, availabilityScore: 55, rubricVersion: 'v1' },
    ],
    assets: ASSETS.filter((a) => a.companySlug === 'meridian-well-automation'),
    watchlistHistory: [],
    notes: [],
  },

  // ── 6. Fjordane Hydraulikk — micro family shop, succession signal ──
  {
    slug: 'fjordane-hydraulikk',
    name: 'Fjordane Hydraulikk AS',
    nameAliases: [],
    formerNames: [],
    website: 'https://www.fjordanehydraulikk.example',
    description:
      'Micro Norwegian hydraulics workshop — cylinder overhaul and HPU service for drilling equipment on the NCS. Founder-led since 1991.',
    hqCountry: 'NO',
    operatingCountries: ['NO'],
    legalIds: { lei: null, noOrgnr: '976543210', ukCrn: null, usCik: null, deHrb: null },
    ticker: null,
    ownershipType: 'family_private',
    ultimateParentSlug: null,
    peSponsor: null,
    peEntryYear: null,
    sizeBand: 'micro',
    status: 'active',
    distressStage: 'none',
    watchlistStage: 'radar',
    facts: [
      { key: 'revenue', value: 'NOK 92m', valueNum: 8.5, unit: 'USD M', fy: 2025, currency: 'NOK', confidence: 'confirmed', source: regnskapRef('976543210', 2025) },
      { key: 'revenue', value: 'NOK 88m', valueNum: 8.1, unit: 'USD M', fy: 2024, currency: 'NOK', confidence: 'confirmed', source: regnskapRef('976543210', 2024) },
      { key: 'ebitda', value: 'NOK 14m', valueNum: 1.3, unit: 'USD M', fy: 2025, currency: 'NOK', confidence: 'confirmed', source: regnskapRef('976543210', 2025) },
      { key: 'employees', value: '34', valueNum: 34, fy: 2025, confidence: 'confirmed', source: brregRef('976543210', 'Enhetsregisteret record', '2026-03-05') },
      { key: 'service_locations', value: 'Florø workshop; offshore service crews (NCS)', confidence: 'confirmed', source: companyRef('fjordanehydraulikk.example', 'About us', '2025-10-01', 'om-oss') },
      { key: 'oem_service_mix', value: '100% service', confidence: 'estimated', estBasis: 'No product sales lines in FY2025 accounts', source: regnskapRef('976543210', 2025) },
      { key: 'key_customers', value: null, confidence: 'not_disclosed', source: null },
      { key: 'founded_year', value: '1991', valueNum: 1991, confidence: 'confirmed', source: brregRef('976543210', 'Enhetsregisteret record', '2026-03-05') },
      { key: 'leadership_ceo', value: 'Odd Arne Sæther (founder, b. 1958)', confidence: 'confirmed', source: brregRef('976543210', 'Enhetsregisteret — roller', '2026-03-05') },
    ],
    events: [
      { id: 151, kind: 'succession_signal', eventDate: '2026-05-30', title: 'Founder-CEO past retirement age with no registered next-generation officers — computed succession signal', confidence: 'estimated', source: null },
    ],
    ownershipHistory: [
      { position: 1, name: 'Fjordane Hydraulikk AS', owner: 'Sæther family', fromDate: '1991', toDate: null, dateConfidence: 'stated', source: brregRef('976543210', 'Enhetsregisteret — historikk', '2026-03-05') },
    ],
    score: {
      fitScore: 58,
      availabilityScore: 44,
      composite: 51,
      rubricVersion: 'v1',
      scoredAt: '2026-06-24T03:10:00Z',
      fitDimensions: [
        { dimension: 'product_adjacency', score: 55, rationale: 'Pure service scope on equipment classes HMH supports; no products.', citations: [{ kind: 'fact', refId: 9505, label: 'service_locations (company page)' }] },
        { dimension: 'aftermarket_attach', score: 75, rationale: '100% service revenue — the entire company is aftermarket.', citations: [{ kind: 'fact', refId: 9506, label: 'oem_service_mix (FY2025 accounts)' }] },
        { dimension: 'technology_differentiation', score: 30, rationale: 'Workshop capability, no proprietary technology.', citations: [{ kind: 'fact', refId: 9505, label: 'service_locations (company page)' }] },
        { dimension: 'size_feasibility', score: 72, rationale: 'Micro band — trivially financeable, but integration yield is small.', citations: [{ kind: 'fact', refId: 9501, label: 'revenue FY2025 (Regnskapsregisteret)' }] },
      ],
    },
    scoreHistory: [
      { scoredAt: '2026-05-24T03:10:00Z', fitScore: 58, availabilityScore: 40, rubricVersion: 'v1' },
      { scoredAt: '2026-06-24T03:10:00Z', fitScore: 58, availabilityScore: 44, rubricVersion: 'v1' },
    ],
    assets: [],
    watchlistHistory: [],
    notes: [],
  },
];

/* ==================================================== light dossiers (8) ==== */
/* Card-grade data + a slim but valid dossier so every card click resolves.     */

interface LightSpec {
  slug: string;
  name: string;
  hqCountry: string;
  operatingCountries: string[];
  subSegments: SubSegment[];
  ownershipType: CompanyCardDto['ownershipType'];
  peSponsor?: string | null;
  peEntryYear?: number | null;
  ticker?: string | null;
  sizeBand: CompanyCardDto['sizeBand'];
  status: CompanyCardDto['status'];
  distressStage: CompanyCardDto['distressStage'];
  watchlistStage: CompanyCardDto['watchlistStage'];
  fit: number | null;
  availability: number | null;
  revenueUsdM: number | null;
  revenueConfidence: Confidence | null;
  employees: number | null;
  oneLiner: string;
  facts: CompanyDossierDto['facts'];
  events: CompanyDossierDto['events'];
  legalIds?: CompanyDossierDto['legalIds'];
}

const LIGHT: LightSpec[] = [
  {
    slug: 'caledonia-subsea-services',
    name: 'Caledonia Subsea Services Ltd',
    hqCountry: 'GB',
    operatingCountries: ['GB', 'NO'],
    subSegments: ['aftermarket_service'],
    ownershipType: 'family_private',
    sizeBand: 'small',
    status: 'active',
    distressStage: 'none',
    watchlistStage: 'radar',
    fit: 62,
    availability: 35,
    revenueUsdM: 22,
    revenueConfidence: 'estimated',
    employees: 140,
    oneLiner: 'UK riser and subsea-stack maintenance house serving North Sea floaters.',
    legalIds: { lei: null, noOrgnr: null, ukCrn: 'SC221100', usCik: null, deHrb: null },
    facts: [
      { key: 'revenue', value: '£17.5m', valueNum: 22, unit: 'USD M', fy: 2025, currency: 'GBP', confidence: 'estimated', estBasis: 'Abridged FY2025 accounts — revenue derived from gross-profit disclosure and stated margin band', source: companiesHouseRef('SC221100', 'Abridged accounts FY2025', '2026-01-31') },
      { key: 'employees', value: '140', valueNum: 140, fy: 2025, confidence: 'confirmed', source: companiesHouseRef('SC221100', 'Abridged accounts FY2025 — employee note', '2026-01-31') },
      { key: 'key_customers', value: null, confidence: 'not_disclosed', source: null },
    ],
    events: [],
  },
  {
    slug: 'bayou-drilling-analytics',
    name: 'Bayou Drilling Analytics LLC',
    hqCountry: 'US',
    operatingCountries: ['US'],
    subSegments: ['automation_digital'],
    ownershipType: 'family_private',
    sizeBand: 'micro',
    status: 'active',
    distressStage: 'none',
    watchlistStage: null,
    fit: null,
    availability: null,
    revenueUsdM: null,
    revenueConfidence: 'not_disclosed',
    employees: 28,
    oneLiner: 'Texas drilling-data startup; universe member, not yet scored.',
    facts: [
      { key: 'revenue', value: null, fy: 2025, confidence: 'not_disclosed', source: null },
      { key: 'employees', value: '28', valueNum: 28, fy: 2025, confidence: 'reported', source: mediaRef('Offshore Engineer', 'Bayou Analytics raises Series A', '2025-10-02', 'bayou-analytics-fixture') },
    ],
    events: [],
  },
  {
    slug: 'hansa-bohrtechnik',
    name: 'Hansa Bohrtechnik GmbH',
    hqCountry: 'DE',
    operatingCountries: ['DE', 'NL'],
    subSegments: ['drilling_equipment_oem'],
    ownershipType: 'family_private',
    sizeBand: 'small',
    status: 'active',
    distressStage: 'none',
    watchlistStage: 'radar',
    fit: 66,
    availability: 30,
    revenueUsdM: 45,
    revenueConfidence: 'confirmed',
    employees: 210,
    oneLiner: 'Hamburg maker of mud-pump modules and solids-control packages.',
    legalIds: { lei: null, noOrgnr: null, ukCrn: null, usCik: null, deHrb: 'HRB 11298' },
    facts: [
      { key: 'revenue', value: '€41.5m', valueNum: 45, unit: 'USD M', fy: 2025, currency: 'EUR', confidence: 'confirmed', source: unternehmensregisterRef('HRB11298', 'Jahresabschluss FY2025', '2026-05-20') },
      { key: 'employees', value: '210', valueNum: 210, fy: 2025, confidence: 'confirmed', source: unternehmensregisterRef('HRB11298', 'Jahresabschluss FY2025', '2026-05-20') },
      { key: 'product_lines', value: 'Mud-pump modules, solids-control packages, HP piping skids', confidence: 'confirmed', source: companyRef('hansa-bohrtechnik.example', 'Produkte', '2026-02-01', 'produkte') },
    ],
    events: [],
  },
  {
    slug: 'straits-rig-power',
    name: 'Straits Rig Power Systems Pte Ltd',
    hqCountry: 'SG',
    operatingCountries: ['SG', 'ID', 'AE'],
    subSegments: ['rig_electrification'],
    ownershipType: 'public',
    ticker: 'SGX: SRP',
    sizeBand: 'small',
    status: 'active',
    distressStage: 'none',
    watchlistStage: 'watch',
    fit: 74,
    availability: 41,
    revenueUsdM: 38,
    revenueConfidence: 'confirmed',
    employees: 150,
    oneLiner: 'SGX-listed shore-power and BESS retrofit integrator for jack-ups.',
    facts: [
      { key: 'revenue', value: 'S$51m', valueNum: 38, unit: 'USD M', fy: 2025, currency: 'SGD', confidence: 'confirmed', source: acraRef('201599887M', 'FY2025 annual report', '2026-03-20') },
      { key: 'electrification_capabilities', value: 'BESS spinning-reserve retrofits (9 jack-ups delivered), shore-power interfaces', confidence: 'confirmed', source: companyRef('straitsrigpower.example', 'Track record', '2026-02-12', 'track-record') },
      { key: 'listed_exchange', value: 'SGX Catalist', confidence: 'confirmed', source: acraRef('201599887M', 'FY2025 annual report', '2026-03-20') },
    ],
    events: [
      { id: 161, kind: 'major_contract_win_loss', eventDate: '2026-06-18', title: 'Two-rig BESS retrofit award, Middle East jack-ups', confidence: 'reported', source: mediaRef('Offshore Engineer', 'Straits Rig Power wins ME retrofit pair', '2026-06-18', 'straits-bess-fixture') },
    ],
  },
  {
    slug: 'vestland-boreteknikk',
    name: 'Vestland Boreteknikk AS',
    hqCountry: 'NO',
    operatingCountries: ['NO'],
    subSegments: ['aftermarket_service'],
    ownershipType: 'state_linked',
    sizeBand: 'small',
    status: 'active',
    distressStage: 'none',
    watchlistStage: 'passed',
    fit: 44,
    availability: 22,
    revenueUsdM: 31,
    revenueConfidence: 'confirmed',
    employees: 120,
    oneLiner: 'Bergen rig-service yard, minority state-linked ownership — passed (ownership complexity).',
    legalIds: { lei: null, noOrgnr: '912345678', ukCrn: null, usCik: null, deHrb: null },
    facts: [
      { key: 'revenue', value: 'NOK 335m', valueNum: 31, unit: 'USD M', fy: 2025, currency: 'NOK', confidence: 'confirmed', source: regnskapRef('912345678', 2025) },
      { key: 'ownership_notes', value: 'Regional development fund holds 34%', confidence: 'confirmed', source: brregRef('912345678', 'Aksjonærregisteret extract', '2026-02-20') },
    ],
    events: [],
  },
  {
    slug: 'pennine-drive-systems',
    name: 'Pennine Drive Systems plc',
    hqCountry: 'GB',
    operatingCountries: ['GB', 'US', 'SG'],
    subSegments: ['components', 'automation_digital'],
    ownershipType: 'public',
    ticker: 'LSE: PDS',
    sizeBand: 'mid',
    status: 'active',
    distressStage: 'none',
    watchlistStage: 'passed',
    fit: 52,
    availability: 18,
    revenueUsdM: 155,
    revenueConfidence: 'confirmed',
    employees: 600,
    oneLiner: 'Listed gearbox and condition-monitoring group; diversified beyond drilling — passed on focus.',
    legalIds: { lei: '213800FIXTUREPDS001', noOrgnr: null, ukCrn: '01234987', usCik: null, deHrb: null },
    facts: [
      { key: 'revenue', value: '£122m', valueNum: 155, unit: 'USD M', fy: 2025, currency: 'GBP', confidence: 'confirmed', source: companiesHouseRef('01234987', 'Annual report FY2025', '2026-03-30') },
      { key: 'sub_segments', value: 'Drilling ≈ 35% of revenue; industrial gears the rest', confidence: 'confirmed', source: companiesHouseRef('01234987', 'Annual report FY2025 — segments', '2026-03-30') },
    ],
    events: [],
  },
  {
    slug: 'lone-star-rig-electrics',
    name: 'Lone Star Rig Electrics Inc',
    hqCountry: 'US',
    operatingCountries: ['US'],
    subSegments: ['rig_electrification'],
    ownershipType: 'family_private',
    sizeBand: 'small',
    status: 'active',
    distressStage: 'early_signal',
    watchlistStage: 'radar',
    fit: 60,
    availability: 58,
    revenueUsdM: 26,
    revenueConfidence: 'estimated',
    employees: 90,
    oneLiner: 'Permian land-rig electrification contractor; layoffs flagged as demand softened.',
    facts: [
      { key: 'revenue', value: '$26m', valueNum: 26, unit: 'USD M', fy: 2025, currency: 'USD', confidence: 'estimated', estBasis: 'Headcount-based estimate at sector revenue-per-employee; no public accounts', source: mediaRef('Offshore Engineer', 'Lone Star Rig Electrics cuts fifth of workforce', '2026-06-02', 'lone-star-layoffs-fixture') },
      { key: 'employees', value: '90', valueNum: 90, fy: 2026, confidence: 'reported', source: mediaRef('Offshore Engineer', 'Lone Star Rig Electrics cuts fifth of workforce', '2026-06-02', 'lone-star-layoffs-fixture') },
    ],
    events: [
      { id: 171, kind: 'mass_layoffs', eventDate: '2026-06-02', title: 'WARN filing — ~20% workforce reduction at Midland shop', jurisdiction: 'US', confidence: 'reported', source: mediaRef('Offshore Engineer', 'Lone Star Rig Electrics cuts fifth of workforce', '2026-06-02', 'lone-star-layoffs-fixture') },
    ],
  },
  {
    slug: 'baltic-mud-systems',
    name: 'Baltic Mud Systems GmbH',
    hqCountry: 'DE',
    operatingCountries: ['DE', 'PL'],
    subSegments: ['drilling_equipment_oem', 'components'],
    ownershipType: 'unknown',
    sizeBand: 'micro',
    status: 'active',
    distressStage: 'pre_distress',
    watchlistStage: 'radar',
    fit: 48,
    availability: 73,
    revenueUsdM: 9,
    revenueConfidence: 'estimated',
    employees: 40,
    oneLiner: 'Rostock mud-system builder with a going-concern flag in its latest accounts.',
    legalIds: { lei: null, noOrgnr: null, ukCrn: null, usCik: null, deHrb: 'HRB 20991' },
    facts: [
      { key: 'revenue', value: '€8.2m', valueNum: 9, unit: 'USD M', fy: 2024, currency: 'EUR', confidence: 'estimated', estBasis: 'FY2024 Jahresabschluss filed late; FY2025 not yet filed', source: unternehmensregisterRef('HRB20991', 'Jahresabschluss FY2024', '2026-01-12') },
      { key: 'ebitda', value: null, fy: 2024, confidence: 'not_disclosed', source: null },
    ],
    events: [
      { id: 181, kind: 'going_concern_flag', eventDate: '2026-01-12', title: 'Auditor going-concern emphasis in FY2024 Jahresabschluss', jurisdiction: 'DE', confidence: 'confirmed', source: unternehmensregisterRef('HRB20991', 'Jahresabschluss FY2024 — Bestätigungsvermerk', '2026-01-12') },
      { id: 182, kind: 'covenant_breach', eventDate: '2026-03-28', title: 'Reported standstill agreement with house bank', confidence: 'reported', source: mediaRef('Offshore Engineer', 'Baltic Mud Systems in talks with lenders', '2026-03-28', 'baltic-mud-standstill-fixture') },
    ],
  },
];

function lightDossier(s: LightSpec): CompanyDossierDto {
  return {
    slug: s.slug,
    name: s.name,
    nameAliases: [],
    formerNames: [],
    subSegments: s.subSegments,
    website: null,
    description: s.oneLiner,
    hqCountry: s.hqCountry,
    operatingCountries: s.operatingCountries,
    legalIds: s.legalIds ?? { lei: null, noOrgnr: null, ukCrn: null, usCik: null, deHrb: null },
    ticker: s.ticker ?? null,
    ownershipType: s.ownershipType,
    ultimateParentSlug: null,
    peSponsor: s.peSponsor ?? null,
    peEntryYear: s.peEntryYear ?? null,
    sizeBand: s.sizeBand,
    status: s.status,
    distressStage: s.distressStage,
    watchlistStage: s.watchlistStage,
    facts: s.facts,
    events: s.events,
    ownershipHistory: [],
    score:
      s.fit === null || s.availability === null
        ? null
        : {
            fitScore: s.fit,
            availabilityScore: s.availability,
            composite: Math.round((s.fit + s.availability) / 2),
            rubricVersion: 'v1',
            scoredAt: '2026-06-24T03:10:00Z',
            fitDimensions: [
              {
                dimension: 'product_adjacency',
                score: s.fit,
                rationale: 'Slim fixture dossier — full dimension breakdown arrives with the real seed (A5/A6).',
                citations: [{ kind: 'fact', refId: 9900, label: 'revenue (latest filed accounts)' }],
              },
            ],
          },
    scoreHistory:
      s.fit === null || s.availability === null
        ? []
        : [
            { scoredAt: '2026-05-24T03:10:00Z', fitScore: s.fit, availabilityScore: Math.max(0, s.availability - 3), rubricVersion: 'v1' },
            { scoredAt: '2026-06-24T03:10:00Z', fitScore: s.fit, availabilityScore: s.availability, rubricVersion: 'v1' },
          ],
    assets: ASSETS.filter((a) => a.companySlug === s.slug),
    watchlistHistory: [],
    notes: [],
  };
}

/* ============================================================ exports ==== */

/** Sub-segments per company — a card field; in the dossier they live as a fact. */
const SUB_SEGMENTS: Record<string, SubSegment[]> = {
  'nordic-drilltech': ['drilling_equipment_oem'],
  'aberdeen-rig-components': ['components', 'aftermarket_service'],
  'gulf-coast-drilling-systems': ['drilling_equipment_oem', 'aftermarket_service'],
  'rheinland-antriebstechnik': ['components', 'rig_electrification'],
  'meridian-well-automation': ['automation_digital'],
  'fjordane-hydraulikk': ['components', 'aftermarket_service'],
  ...Object.fromEntries(LIGHT.map((s) => [s.slug, s.subSegments])),
};

/** Card meta for the six full-dossier companies. */
const FULL_CARD_META: Record<string, { revenueUsdM: number | null; revenueConfidence: Confidence | null; employeeCount: number | null; oneLiner: string }> = {
  'nordic-drilltech': { revenueUsdM: 128, revenueConfidence: 'confirmed', employeeCount: 410, oneLiner: 'Family-owned Norwegian pipe-handling OEM with a growing aftermarket book.' },
  'aberdeen-rig-components': { revenueUsdM: 41, revenueConfidence: 'confirmed', employeeCount: 180, oneLiner: 'PE-owned UK BOP-components and recertification house — hold maturing, sale reportedly explored.' },
  'gulf-coast-drilling-systems': { revenueUsdM: 96, revenueConfidence: 'confirmed', employeeCount: 350, oneLiner: 'Chapter 11 drawworks maker — §363 auction of the GoM aftermarket book underway.' },
  'rheinland-antriebstechnik': { revenueUsdM: 187, revenueConfidence: 'estimated', employeeCount: 720, oneLiner: 'German drives division designated non-core; parent running a strategic review.' },
  'meridian-well-automation': { revenueUsdM: 28, revenueConfidence: 'reported', employeeCount: 95, oneLiner: 'Singapore drilling-automation software house; PE hold in year six.' },
  'fjordane-hydraulikk': { revenueUsdM: 8.5, revenueConfidence: 'confirmed', employeeCount: 34, oneLiner: 'Micro NCS hydraulics workshop, founder-led — succession signal flagged.' },
};

export function subSegmentsOf(slug: string): SubSegment[] {
  return SUB_SEGMENTS[slug] ?? [];
}

export const DOSSIERS: Record<string, CompanyDossierDto> = Object.fromEntries(
  [
    ...DOSSIER_LIST.map((d) => ({ ...d, subSegments: SUB_SEGMENTS[d.slug] ?? [] })),
    ...LIGHT.map(lightDossier),
  ].map((d) => [d.slug, d])
);

export const COMPANY_CARDS: CompanyCardDto[] = [...DOSSIER_LIST, ...LIGHT.map(lightDossier)].map((d) => {
  const light = LIGHT.find((s) => s.slug === d.slug);
  const meta = light
    ? { revenueUsdM: light.revenueUsdM, revenueConfidence: light.revenueConfidence, employeeCount: light.employees, oneLiner: light.oneLiner }
    : FULL_CARD_META[d.slug];
  return {
    slug: d.slug,
    name: d.name,
    hqCountry: d.hqCountry,
    subSegments: subSegmentsOf(d.slug),
    ownershipType: d.ownershipType,
    peSponsor: d.peSponsor,
    sizeBand: d.sizeBand,
    status: d.status,
    distressStage: d.distressStage,
    watchlistStage: d.watchlistStage,
    fitScore: d.score?.fitScore ?? null,
    availabilityScore: d.score?.availabilityScore ?? null,
    revenueUsdM: meta?.revenueUsdM ?? null,
    revenueConfidence: meta?.revenueConfidence ?? null,
    employeeCount: meta?.employeeCount ?? null,
    oneLiner: meta?.oneLiner ?? null,
  };
});
